import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { auth, ApiError } from '@/api/client';

/**
 * F2 — Self-service change-password modal.
 *
 * Enforces the same policy the backend does; the strength meter renders
 * live feedback, but the authoritative check happens server-side.
 */
export function ChangePasswordDialog({
  open,
  onOpenChange,
  userEmail,
  userFullName,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  userEmail: string;
  userFullName: string;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNext, setShowNext] = useState(false);
  const [policyOk, setPolicyOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setShowNext(false);
    setPolicyOk(false);
    setError(null);
    setSubmitting(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!current) {
      setError('Please enter your current password.');
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (!policyOk) {
      setError('New password does not meet the strength policy.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await auth.changePassword(current, next);
      toast.success(res.message || 'Password updated.');
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.details as { errors?: string[] } | undefined)?.errors?.join(' ') || err.message
        : 'Could not update password. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      title="Change password"
      description="Choose a strong new password. Your existing sessions on other devices remain active until they expire naturally."
      width={520}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="block text-[11.5px] font-semibold text-text-2 mb-1">Current password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            maxLength={200}
            className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          />
        </label>

        <label className="block">
          <span className="block text-[11.5px] font-semibold text-text-2 mb-1">New password</span>
          <div className="relative">
            <input
              type={showNext ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={200}
              className="w-full h-10 px-3 pr-9 rounded-sm border border-border-strong text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            />
            <button
              type="button"
              onClick={() => setShowNext((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-4 hover:text-text-2"
              aria-label={showNext ? 'Hide password' : 'Show password'}
            >
              {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </label>

        <PasswordStrengthMeter
          password={next}
          userInputs={[userEmail, userFullName]}
          onPolicyChange={setPolicyOk}
        />

        <label className="block">
          <span className="block text-[11.5px] font-semibold text-text-2 mb-1">Confirm new password</span>
          <input
            type={showNext ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            maxLength={200}
            className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm focus:outline-none focus:ring-2 focus:ring-navy"
          />
        </label>

        {error && (
          <div className="text-[11.5px] text-critical bg-critical-soft border border-critical/30 rounded-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 px-3 rounded-sm text-[12.5px] text-text-2 hover:bg-soft"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !policyOk || next !== confirm}
            className="h-9 px-4 rounded-sm bg-navy text-white text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Updating' : 'Update password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
